import React from 'react';
import {  useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/state';

//Component
import { GridContainer, GridItem, CustomButton, PhoneNoTextInput, DynamicTypography } from '../../components';
import Typography from '@material-ui/core/Typography';
import QRCode from 'qrcode.react';

//Helper
import { getVerbiage, isValidateEmail, isValidatePhoneNo } from '../../utils/stringHelper';
import { IconButton, Snackbar, TextField } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ARIA_SuccessCode } from 'models';
import { subscribeRoomForNotification } from 'reducers/reservation';
import { createGuestPhoneNoAPI, updateGuestPhoneNoAPI } from 'services/guestService';
import { createPreRegistrationNotifyEmailAPI, updatePreRegistrationNotifyEmailAPI } from 'services/preRegistrationService';
import { generateOTPPinAPI } from 'services/reservationService';
import { getApplicationMode } from 'utils/applicationHelper';
import { MobileApplicationMode } from 'utils/constantHelper';
import { postErrorLog } from 'services/errorLogService';

interface IProps {
  onRequestMobileKey: () => void;
}

export const RegistrationComplete: React.FC<IProps> = ({ onRequestMobileKey }) => {
  const dispatch = useDispatch();

  const { appConfig } = useSelector((state: RootState) => state.appConfigState);
  const { verbiages } = useSelector((state: RootState) => state.verbiages);
  const { selectedHotel } = useSelector((state: RootState) => state.hotelState);
  const { selectedReservation } = useSelector((state: RootState) => state.reservationState);
  const { countryList } = useSelector((state: RootState) => state.country);

  const [applicationMode, _] = React.useState(getApplicationMode);

  const [message, setMessage] = React.useState('');

  const [email, setEmail] = React.useState<string>('');
  const [emailHelptext, setEmailHelptext] = React.useState('');

  const [fullPhoneNo, setFullPhoneNo] = React.useState<string>('');
  const [phoneNoHelpText, setPhoneNoHelpText] = React.useState<string>('');

  const [countryId, setCountryId] = React.useState<number|undefined>(undefined);
  const [phoneNumber, setPhoneNumber] = React.useState<string>('');
  const [hasSubmitNotify, setHasSubmitNotify] = React.useState(false);
  const [changeEmail, setChangeEmail] = React.useState(false);

  const [isLoading, setIsLoading] = React.useState(false);

  const [pin, setPin] = React.useState<string>('');
  
  React.useEffect(() => {
    const preregisteredCompleted = async () => {
      if (selectedReservation != undefined) {
        if (
          selectedReservation.guest &&
          selectedReservation.guest.emails != undefined &&
          selectedReservation.guest.emails.length > 0 &&
          selectedReservation.guest.emails[0].email != undefined
        ) {
          setEmail(selectedReservation.guest.emails[0].email);
        }

        if (
          selectedReservation.guest &&
          selectedReservation.guest.contacts != undefined &&
          selectedReservation.guest.contacts.length > 0 &&
          selectedReservation.guest.contacts[0].phoneNo != undefined           
        ) {
          const contact = selectedReservation.guest.contacts[0];
          setPhoneNumber(selectedReservation.guest.contacts[0].phoneNo);         
          const matchedCountry = countryList.find(country => country.id === contact.countryId);
          setCountryId(matchedCountry ? matchedCountry.id: undefined);
          setFullPhoneNo(matchedCountry? matchedCountry.phoneCode + ' ' + selectedReservation.guest.contacts[0].phoneNo: selectedReservation.guest.contacts[0].phoneNo);
        }

        if (selectedReservation.subscribeEmail) {
          setEmail(selectedReservation.subscribeEmail);
        }
      }
    };

    preregisteredCompleted();
  }, [selectedReservation]);

  const handleNotify = async () => {
    const requiresEmail = appConfig?.reservationConfig.isEmailRequire;
    const requiresPhone = appConfig?.reservationConfig.isPhoneNoRequire;
  
    let emailValid = true;
    let phoneValid = true;
  
    if (requiresEmail && !isValidateEmail(email)) {
      setMessage(`${getVerbiage(verbiages, 'webmsg004')}`); // Invalid Email
      emailValid = false;
    }
  
    if (requiresPhone && !isValidatePhoneNo(fullPhoneNo)) {
      setMessage(`${getVerbiage(verbiages, 'webmsg010')}`); // Invalid Phone Number
      phoneValid = false;
    }
  
    if (!emailValid || !phoneValid) {
      return;
    }
  
    if (!selectedHotel || !selectedReservation) return;
  
    setIsLoading(true);
  
    const tasks: Promise<any>[] = [];
  
    let shouldDispatchSubscribe = false;
  
    // Email update or create
    if (requiresEmail) {
      if (selectedReservation.guest.emails?.[0]?.emailId) {
        tasks.push(
          updatePreRegistrationNotifyEmailAPI({
            profileId: selectedReservation.guest.profileId,
            reservationId: selectedReservation.id,
            emailId: selectedReservation.guest.emails[0].emailId!,
            email,
          })
        );
      } else {
        shouldDispatchSubscribe = true;
        tasks.push(
          createPreRegistrationNotifyEmailAPI({
            profileId: selectedReservation.guest.profileId,
            reservationId: selectedReservation.id,
            email
          })
        );
      }
    }
  
    // Phone update or create
    if (requiresPhone) {
      if (selectedReservation.guest.contacts?.[0]?.phoneId) {
        tasks.push(
          updateGuestPhoneNoAPI({
            profileId: selectedReservation.guest.profileId,
            phoneId: selectedReservation.guest.contacts[0].phoneId,
            countryId,
            contactNo: phoneNumber,
          })
        );
      } else {        
        tasks.push(
          createGuestPhoneNoAPI({
            profileId: selectedReservation.guest.profileId,
            countryId,
            contactNo: phoneNumber,
          })
        );
      }
    }
  
    try {
      const responses = await Promise.all(tasks);
  
      const allSuccessful = responses.every((res) => res.resultCode === ARIA_SuccessCode);
  
      if (allSuccessful) {
        if (shouldDispatchSubscribe) {
          dispatch(subscribeRoomForNotification());
        }
        setHasSubmitNotify(true);
        setIsLoading(false);
        setChangeEmail(false);
      } else {
        setIsLoading(false);
        setMessage(`${getVerbiage(verbiages, 'webmsg014')}`);
      }
    } catch (err) {
      setIsLoading(false);
      setMessage(`${getVerbiage(verbiages, 'webmsg014')}`);
      postErrorLog({
        confirmationNo: selectedReservation?.confirmationNo,
        errorMessage: JSON.stringify(err),
      });
    }
  };

  const handleGenerateOTP = async () => {
    if (selectedReservation != undefined) {
      setIsLoading(true);

      generateOTPPinAPI({
        guid: selectedReservation.guid
      })
        .then((response) => {
          if (response.resultCode === ARIA_SuccessCode) {
            setPin(response.result.pin);
            setIsLoading(false);
          } else {
            setIsLoading(false);
            setMessage(`${getVerbiage(verbiages, 'webmsg014')}`);
            // setMessage('Oposs... Something when wrong, please try again.');
          }
        })
        .catch((err) => {
          setIsLoading(false);
          setMessage(`${getVerbiage(verbiages, 'webmsg014')}`);
          postErrorLog({
            confirmationNo: selectedReservation?.confirmationNo,
            errorMessage: JSON.stringify(err)
          });
          // setMessage('Oposs... Something when wrong, please try again.');
        });
    }
  };

  function checkIsReadyToSubmit(): boolean {
    var emailDisable = true;
    var phoneDisable = true;

    if (appConfig?.reservationConfig.isEmailRequire && isValidateEmail(email)) {
      emailDisable = false;
    }

    if(appConfig?.reservationConfig.isPhoneNoRequire && isValidatePhoneNo(fullPhoneNo)) {
      phoneDisable = false
    }

    return emailDisable && phoneDisable;
  }

  const handleClose = () => {
    setMessage('');
  };

  return (
    <GridContainer
      spacing={2}
      alignItems="center"
      justifyContent="center"
      direction="column"
      style={{ paddingTop: '18px' }}
    >
      <GridItem xs={12}>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={message !== ''}
          onClose={handleClose}
          autoHideDuration={5000}
          message={message}
          action={
            <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </GridItem>

      {/* Room is ready */}
      {selectedReservation !== undefined &&
      selectedReservation.room !== undefined &&
      selectedReservation.room.isRoomReady &&
      selectedReservation.room.number !== '' ? (
        <>
          <GridItem xs={12}>
            <div style={{ padding: '3px', backgroundColor: 'white' }}>
              {appConfig?.reservationConfig.displayQRCode ? (
                <QRCode
                  value={`${window.location.origin.toLowerCase()}/${selectedHotel?.code}?resKey=${
                    selectedReservation.guid
                  }`}
                />              
                ) : null}              
            </div>
            {appConfig?.reservationConfig.displayRoomNo ? (
              <Typography color="textPrimary" align="center" variant="h6">
                <b>{`Room ${selectedReservation.room.number}`}</b>
              </Typography>
            ) : null}
          </GridItem>
          <GridItem xs={12}>
            <DynamicTypography 
              color="textPrimary" 
              align="center" 
              variant="body1" 
              content={`${getVerbiage(verbiages, 'weblbl065')}`}> {/* Your room is ready */}
            </DynamicTypography>
          </GridItem>

          {appConfig?.webOTPToCollectKeyFromKiosk ? (
            <>
              <GridItem xs={12}>
                {pin != '' ? (
                  <DynamicTypography
                    color="textPrimary"
                    align="center"
                    variant="h6"
                    content={`<b>${pin}</b>`}
                  ></DynamicTypography>
                ) : null}
              </GridItem>
              <GridItem xs={12}>
                <CustomButton disabled={isLoading} onClick={handleGenerateOTP}>
                  {getVerbiage(verbiages, 'webbtn013')}
                </CustomButton>
              </GridItem>
            </>
          ) : null}

          {applicationMode == MobileApplicationMode ? (
            <GridItem xs={12}>
              <CustomButton disabled={!isValidateEmail(email) || changeEmail || isLoading} onClick={onRequestMobileKey}>
                {/* Request Mobile Key */}
                {getVerbiage(verbiages, 'weblbl086')}
              </CustomButton>
            </GridItem>
          ) : null}

          <GridItem xs={12}>          
            <DynamicTypography
              color="textPrimary"
              align="center"
              variant="body1"
              content={`${getVerbiage(
                verbiages,
                applicationMode == MobileApplicationMode ? 'weblbl079' : 'weblbl066' 
              )}`}
            ></DynamicTypography>
          </GridItem>

          {/* Email input */}
          {appConfig?.reservationConfig.isEmailRequire ? (
            <GridItem xs={12}>
              <form noValidate autoComplete="off">
                <TextField
                  required
                  disabled={!changeEmail}
                  error={emailHelptext != ''}
                  helperText={emailHelptext}
                  style={{ width: '80vw' }}
                  id="outlined-required"
                  label={`${getVerbiage(verbiages, 'weblbl004')}`}
                  variant="outlined"
                  value={email}
                  onChange={(e) => {
                    if (e.currentTarget.value == '') {
                      setEmailHelptext(`${getVerbiage(verbiages, 'webmsg003')}`);
                    } else if (!isValidateEmail(e.currentTarget.value))
                      setEmailHelptext(`${getVerbiage(verbiages, 'webmsg004')}`);
                    else {
                      setEmailHelptext('');
                    }
                    setEmail(e.currentTarget.value);
                  }}
                />
              </form>
            </GridItem>
          ) : null}

          {/* phone no input */}
          {appConfig?.reservationConfig.isPhoneNoRequire ? (
            <GridItem xs={12}>
              <PhoneNoTextInput
                style={{ marginTop: '18px', width: '80vw' }}
                value={fullPhoneNo}
                disabled={changeEmail}
                errorHelperText={phoneNoHelpText}
                onPhoneNoChange={(number) => {
                  if (!isValidatePhoneNo(number)) {
                    setPhoneNoHelpText(getVerbiage(verbiages, 'webmsg010'));
                  } else {
                    setPhoneNoHelpText('');
                  }
                  setFullPhoneNo(number);
                  const [phoneCode, phoneNo] = number.split(' ');
                  const matchedCountry = countryList.find(country => country.phoneCode === phoneCode);
                  setCountryId(matchedCountry ? matchedCountry.id: undefined);
                  setPhoneNumber(phoneNo);
                }}
              />
            </GridItem>
          ) : null}

          {
            appConfig?.reservationConfig.isEmailRequire || appConfig?.reservationConfig.isPhoneNoRequire ? (
              !changeEmail ? (
                <GridItem xs={12}>
                  <CustomButton onClick={() => setChangeEmail(true)}>{getVerbiage(verbiages, 'weblbl085')}</CustomButton> {/*Update*/}
                </GridItem>
              ) : (
                <GridItem>
                  <CustomButton disabled={checkIsReadyToSubmit() || isLoading} onClick={handleNotify}>
                    {getVerbiage(verbiages, 'webbtn012')} {/*Use email*/}
                  </CustomButton>
                </GridItem>
              )
            ): null
          }
          

          <GridItem xs={12} style={{ marginBottom: '48px' }}>
            <Typography color="textPrimary" align="center" variant="body1">
              {`${getVerbiage(verbiages, 'weblbl067')}`} {/*Thank You*/}
            </Typography>
          </GridItem>
        </>
      ) : (
        <>
          <GridItem xs={12}>
            <DynamicTypography
              color="textPrimary"
              align="center"
              variant="subtitle1"
              content={
                !hasSubmitNotify ? `${getVerbiage(verbiages, 'weblbl068')}` : `${getVerbiage(verbiages, 'weblbl067')}`
              }
            />
          </GridItem>
          <GridItem xs={12}>
            <DynamicTypography
              color="textPrimary"
              align="center"
              variant="body1"
              content={
                !hasSubmitNotify ? `${getVerbiage(verbiages, 'weblbl069')}` : `${getVerbiage(verbiages, 'weblbl080')}`
              } // We will send an email notification once your room is ready.
            />
          </GridItem>

          {/* Email input */}
          {appConfig?.reservationConfig.isEmailRequire ? (
            <GridItem xs={12}>
              <form noValidate autoComplete="off">
                <TextField
                  required
                  disabled={!changeEmail}
                  error={emailHelptext != ''}
                  helperText={emailHelptext}
                  style={{ width: '80vw' }}
                  id="outlined-required"
                  label={`${getVerbiage(verbiages, 'weblbl004')}`}
                  variant="outlined"
                  value={email}
                  onChange={(e) => {
                    if (e.currentTarget.value == '') {
                      setEmailHelptext(`${getVerbiage(verbiages, 'webmsg003')}`);
                    } else if (!isValidateEmail(e.currentTarget.value))
                      setEmailHelptext(`${getVerbiage(verbiages, 'webmsg004')}`);
                    else {
                      setEmailHelptext('');
                    }
                    setEmail(e.currentTarget.value);
                  }}
                />
              </form>
            </GridItem>
          ) : null}

          {/* phone no input */}
          {appConfig?.reservationConfig.isPhoneNoRequire ? (
            <GridItem xs={12}>
              <PhoneNoTextInput
                style={{ marginTop: '18px', width: '80vw' }}
                value={fullPhoneNo}
                disabled={changeEmail}
                errorHelperText={phoneNoHelpText}
                onPhoneNoChange={(number) => {
                  if (!isValidatePhoneNo(number)) {
                    setPhoneNoHelpText(getVerbiage(verbiages, 'webmsg010'));
                  } else {
                    setPhoneNoHelpText('');
                  }
                  setFullPhoneNo(number);
                  const [phoneCode, phoneNo] = number.split(' ');
                  const matchedCountry = countryList.find(country => country.phoneCode === phoneCode);
                  setCountryId(matchedCountry ? matchedCountry.id: undefined);
                  setPhoneNumber(phoneNo);
                }}
              />
            </GridItem>
          ) : null}

          {
            appConfig?.reservationConfig.isEmailRequire || appConfig?.reservationConfig.isPhoneNoRequire ? (
              !changeEmail ? (
                <GridItem xs={12}>
                  <CustomButton onClick={() => setChangeEmail(true)}>{getVerbiage(verbiages, 'weblbl085')}</CustomButton> {/*Update*/}
                </GridItem>
              ) : (
                <GridItem>
                  <CustomButton disabled={checkIsReadyToSubmit() || isLoading} onClick={handleNotify}>
                    {getVerbiage(verbiages, 'webbtn012')} {/*Use email*/}
                  </CustomButton>
                </GridItem>
              )
            ): null
          }
        </>
      )}
    </GridContainer>
  );
};
